/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
/** biome-ignore-all lint/style/noNestedTernary: no nested ternary */

import cx from 'classnames';
import {DateTime} from 'luxon';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useSelector, useStore} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {
  selectEventOptionAllowMaybeRsvps,
  selectRSVPDeadline,
  selectRsvpDeadlineIsStrict,
} from '~/common/fabric/reducers/eviteEvent/selectors';
import {UPSELL_MODES, UpsellModal} from '~/common/fabric/UpsellModal/UpsellModal';
import {useIsLargeAndUp, useIsLargishAndUp} from '~/common/hooks/useMatchQuery';
import {getUsedAsTypeName, isCanceled} from '~/common/utils/event_utils';
import {
  isFabricWebview as isFabricWebviewCheck,
  isLegacyFabricWebview as isLegacyFabricWebviewCheck,
} from '~/common/utils/webview';
import {GuestReaction} from '~/GuestReaction/GuestReaction';
import {StickyEditEventMoreOptions} from '~/ViewInvitation/common/StickyEditEventMoreOptions';
import {submitRSVP} from '~/ViewInvitation/reducers/apiActions';
import {selectEventHasPassed} from '~/ViewInvitation/reducers/eventDetails';
import {MoreDetailsIndicator} from '~/ViewInvitation/routes/Landing/MoreDetailsIndicator';
import {selectIsPremium} from '~/ViewInvitation/selectors/eventDetails';
import {webviewSendRsvpDetails} from '~/ViewInvitation/webview/webview';
import {Button} from '~library/atoms/Button';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {PremiumIcon} from '~library/svg/PremiumIcon';
import {getUserData} from '~library/utils/auth/utils';
import {premiumColor} from '~libSass/base/_exports.sass';
import {GuestRsvpActions} from '../GuestRsvpActions';
import {ManageEventCTA} from '../ManageEventCTA';
import {RSVPModalTestAB} from '../RSVPModalTestAB';
import {RSVPDetails} from './RSVPDetails';

import './StickyEditEvent.sass';

const isLegacyFabricWebview = isLegacyFabricWebviewCheck(); // true if page is in any webview (old or new)
const fabricWebviewHasEventCtaBridges = isFabricWebviewCheck(['viewInvitationOpenEventOverview']); // true only for new app webview
const shouldShowRsvpPanel = !isLegacyFabricWebview || fabricWebviewHasEventCtaBridges; // show RSVP panel if NOT webview, or if webview has RSVP bridge function
const DESKTOP_SCROLL_THRESHOLD = 185;
const MW_SCROLL_THRESHOLD = 172;

export const StickyEditEvent = ({
  forceShow,
  setShowingRSVPModal,
  setGoogleAdsKey,
  setAdsPageType,
}) => {
  const location = useLocation();
  const store = useStore();
  const isLargishAndUp = useIsLargishAndUp();
  const isLargeAndUp = useIsLargeAndUp();

  const hostIds = window.evite_event.host_ids || [];
  const pendingHostIds = window.evite_event.pending_host_ids || [];
  const isCohost = hostIds.includes(window.user_id) && !pendingHostIds.includes(window.user_id);
  const isHostOrCohost = window.user_id === window.evite_event.host_id || isCohost;

  const rsvpOff = useSelector((state) => state.eventDetails.rsvpOff);
  const {isWebview} = window;

  const searchParams = new URLSearchParams(location.search);
  const gid = searchParams.get('gid');

  const eventId = useSelector((state) => state.eventDetails?.eventId ?? null);
  const userData = getUserData(isWebview);
  const {name, email} = useSelector((state) => state.user);
  const guestId = userData.guest_id || userData.guestId || gid || window.guest_id;

  if (!userData.userName) userData.userName = name;
  if (!userData.email) userData.email = email;
  const hasGuestReacted = window.evite_event?.rsvp?.comments;

  const [hideGuestReactions, setHideGuestReactions] = useState(false);

  const origin = useSelector((state) => state.eventDetails?.origin || state.eviteEvent.origin);
  const eventStatus = useSelector((state) => state.eventDetails?.status || state.eviteEvent.status);
  const usedAs = useSelector((state) => state.eventDetails?.usedAs || state.eviteEvent?.used_as);
  const rsvpResponse = useSelector((state) => state.rsvp?.rsvpResponse);
  const hasRsvpd = useSelector((state) => state.rsvp?.hasRsvpd);
  const isPremium = useSelector(selectIsPremium);
  const ctaWordings = getUsedAsTypeName(usedAs).toLowerCase();
  const loadedDefaultRsvp = useRef(false);
  const allowMaybeRSVPs = useSelector(selectEventOptionAllowMaybeRsvps);
  const rsvpDeadline = useSelector(selectRSVPDeadline);
  const strictRSVPDeadline = useSelector(selectRsvpDeadlineIsStrict);
  const deadlineHasPassed = rsvpDeadline ? DateTime.now() > DateTime.fromISO(rsvpDeadline) : false;

  const [updatingRsvp, setUpdatingRsvp] = useState(false);
  const [wipRsvpStatus, setWipRsvpStatus] = useState();
  const urlParams = new URLSearchParams(window.location.search);
  const showRsvpOverlayOnLoad = urlParams.get('rsvp') === '1';
  const [rsvpOverlayOpen, setRsvpOverlayOpen] = useState(showRsvpOverlayOnLoad);

  const eventHasPassed = useSelector(selectEventHasPassed);
  const eventIsCanceled = isCanceled(eventStatus);
  const [hideMore, setHideMore] = useState(false);
  const [showUpsellModal, setShowUpsellModal] = useState(false);

  const [atTop, setAtTop] = useState(true);
  const toggleShowRSVP = () => {
    setAtTop(window.scrollY < (isLargishAndUp ? DESKTOP_SCROLL_THRESHOLD : MW_SCROLL_THRESHOLD));
    setHideMore(window.scrollY > DESKTOP_SCROLL_THRESHOLD);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    document.addEventListener('scroll', toggleShowRSVP);

    if (window.location.href.includes('/messages')) {
      setHideGuestReactions(true);
    }

    return () => {
      document.removeEventListener('scroll', toggleShowRSVP);
    };
  }, []);

  const rsvpDrawer = useRef(null);

  useEffect(() => {
    const footer = document.getElementById('footer');
    if (!footer) return;
    if (isLargishAndUp) {
      footer.style.paddingBottom = '5rem';
    } else {
      footer.style.paddingBottom = '11rem';
    }
  }, [isLargishAndUp]);

  useEffect(() => {
    setShowingRSVPModal(rsvpOverlayOpen);
  }, [rsvpOverlayOpen, setShowingRSVPModal]);

  useEffect(() => {
    if (!loadedDefaultRsvp.current) {
      const params = new URLSearchParams(location.search);
      const status = params.get('rsvp_response');
      if (status) {
        setWipRsvpStatus(`${status[0].toUpperCase()}${status.substring(1)}`);
        setRsvpOverlayOpen(true);
      }
    }
  }, [location]);

  const onSubmitRSVP = (payload, doNotClose) =>
    new Promise((resolve, reject) => {
      if (!payload.name) {
        toastAlert('Name is required', {
          type: 'error',
          icon: <AlertLineIcon size={0.75} />,
        });
        reject();
      } else if (!payload.email) {
        toastAlert('Email address is required', {
          type: 'error',
          icon: <AlertLineIcon size={0.75} />,
        });
        reject();
      } else {
        store
          .dispatch(
            submitRSVP({
              event_id: eventId,
              guest_id: guestId,
              payload,
              successCallback: () => {
                webviewSendRsvpDetails(payload);
                if (!doNotClose) {
                  setRsvpOverlayOpen(false);
                }
                resolve();
              },
              failureCallback: async (dispatch, getState, results) => {
                let message = results?.results?.message;
                if (!message) {
                  try {
                    const request = results?.response || results;
                    if (request instanceof Response) {
                      const json = await results?.response?.json();
                      message = json?.message;
                    } else {
                      evite.error(`Failed to find request in results: ${eventId}`);
                      message = 'Unable to RSVP, please contact support';
                    }
                  } catch (e) {
                    evite.error('Failed to find error message', e);
                    message = 'Unable to RSVP, please contact support';
                  }
                }
                toastAlert(message, {
                  type: 'error',
                  icon: <AlertLineIcon size={0.75} />,
                  style: {whiteSpace: 'pre-wrap'},
                });
                reject(message);
              },
            })
          )
          .then(() => {
            setAdsPageType('ads-rsvp-done');
            setGoogleAdsKey(crypto.randomUUID());
            resolve();
          })
          .catch(reject);
      }
    });

  const showReaction =
    !hideGuestReactions && rsvpOff && isWebview === undefined && !hasGuestReacted && !hasRsvpd;
  const showStickyBar =
    showReaction || // Show if there is a reaction element to show
    isHostOrCohost || // or show if the user is the host or a cohost
    (!isHostOrCohost && !rsvpOff); // or show if the user is NOT the host or cohost and RSVP is not off

  const shouldRenderElement =
    isLargishAndUp ||
    (showStickyBar && // or: (if showStickyBar (see above) is true
      // and if it's either a non-host/cohost guest with the rsvp panel visible or it's a host/cohost that isn't updating their RSVP status)
      !isHostOrCohost &&
      shouldShowRsvpPanel) ||
    (isHostOrCohost && !updatingRsvp);

  const showRemoveAdsBtn = !isPremium && isLargeAndUp && !isWebview && !eventHasPassed;

  const removeAdsPostSend = useCallback(() => {
    window.location.href = `/invitation/${eventId}/review?upgrade=true`;
  }, [eventId]);

  const removeAdsCallback = useCallback(() => {
    removeAdsPostSend();
  }, [removeAdsPostSend]);

  const hideMoreDetails = hideMore || (isPremium && !forceShow);

  return (
    <>
      {/* If ANY of the conditions in the div are true, render the div, otherwise don't render an empty div */}
      {shouldRenderElement
        ? <div
            className={cx('view-invitation__sticky-wrapper', {
              open: !atTop || forceShow,
            })}
          >
            <div className={cx('more-details--sticky-bar', {hide: hideMoreDetails})}>
              <MoreDetailsIndicator preview={false} />
            </div>
            {showStickyBar && (
              <div
                className={`view-invitation__sticky-ctas
                          ${!atTop || forceShow ? 'open' : ''}
                          ${!rsvpOff && (!hasRsvpd || updatingRsvp) ? 'show-rsvp-ctas' : ''}`}
                id="viewInvitationCTAWrapper"
                ref={rsvpDrawer}
              >
                {!isHostOrCohost &&
                  shouldShowRsvpPanel &&
                  (showReaction
                    ? <GuestReaction
                        rsvpDrawer={rsvpDrawer}
                        eventId={eventId}
                        guestId={guestId}
                        eviteType="event"
                      />
                    : <div className="view-invitation__rsvp-wrapper">
                        {eventHasPassed
                          ? <Typography variant="paragraph3" data-qa-id="event-already-occurred">
                              This event has already occurred.
                            </Typography>
                          : eventIsCanceled
                            ? <Typography variant="paragraph3" data-qa-id="event-canceled">
                                This event has been canceled.
                              </Typography>
                            : !hasRsvpd || updatingRsvp
                              ? <GuestRsvpActions
                                  hasRsvpd={hasRsvpd}
                                  setUpdatingRsvp={setUpdatingRsvp}
                                  setWipRsvpStatus={setWipRsvpStatus}
                                  setRsvpOverlayOpen={setRsvpOverlayOpen}
                                  origin={origin}
                                  allowMaybeRSVPs={allowMaybeRSVPs}
                                  rsvpDeadline={rsvpDeadline}
                                  deadlineHasPassed={deadlineHasPassed}
                                  strictRSVPDeadline={strictRSVPDeadline}
                                />
                              : <RSVPDetails
                                  rsvpResponse={rsvpResponse}
                                  setUpdatingRsvp={setUpdatingRsvp}
                                  deadlineHasPassed={deadlineHasPassed}
                                  strictRSVPDeadline={strictRSVPDeadline}
                                />}
                      </div>)}
                {isHostOrCohost && !updatingRsvp
                  ? <div className="sticky-cta__host-actions">
                      <ManageEventCTA
                        renderForWebview={fabricWebviewHasEventCtaBridges}
                        eventId={eventId}
                        ctaWordings={ctaWordings}
                        isLargeAndUp={isLargeAndUp}
                        standaloneBtn={isWebview || eventHasPassed}
                      />
                      {showRemoveAdsBtn && (
                        <Button
                          className="sticky-cta__remove-ads-btn"
                          variant="secondary"
                          size="large"
                          data-qa-id="sticky-cta-remove-ads-btn"
                          onClick={() => setShowUpsellModal(true)}
                        >
                          <PremiumIcon ratio={0.125} color={premiumColor} />
                          Remove ads
                        </Button>
                      )}
                      {!fabricWebviewHasEventCtaBridges && !eventHasPassed && !rsvpOff && (
                        <StickyEditEventMoreOptions
                          eventId={eventId}
                          onRemoveAdsClick={() => setShowUpsellModal(true)}
                        />
                      )}
                    </div>
                  : null}
              </div>
            )}
          </div>
        : null}
      <RSVPModalTestAB
        status={wipRsvpStatus}
        onClose={() => {
          setRsvpOverlayOpen(false);
          setUpdatingRsvp(false);
        }}
        open={rsvpOverlayOpen}
        onSubmitRSVP={onSubmitRSVP}
      />
      {showUpsellModal && (
        <UpsellModal
          mode={UPSELL_MODES.REMOVE_ADS}
          closeOverlay={() => setShowUpsellModal(false)}
          toggleUpsellModal={() => setShowUpsellModal(!showUpsellModal)}
          hideCloseButton={false}
          onConfirmAction={removeAdsCallback}
          onDenyAction={() => setShowUpsellModal(!showUpsellModal)}
        />
      )}
    </>
  );
};
